import React from 'react';
import { Alert, Box, Button as MuiButton, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ProductGrid from '../components/products/ProductGrid';
import { ROUTES } from '../constants';
import { addToCart } from '../features/cartSlice';
import { clearMyWishlist, removeFromWishlist } from '../features/wishlistSlice';
import { useAppDispatch, useAppSelector } from '../hooks';
import { Product } from '../types';

const WishlistPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);
  const { wishlist, loading, mutating, error } = useAppSelector((state) => state.wishlist);

  const products = (wishlist?.items || [])
    .map((item) => item.product)
    .filter((product) => Boolean(product));

  const handleAddToCart = async (product: Product) => {
    if (!token) {
      navigate(ROUTES.LOGIN);
      return;
    }
    await dispatch(addToCart({ productId: product._id, quantity: 1 }));
  };

  const handleToggleWishlist = async (product: Product) => {
    if (!token) {
      navigate(ROUTES.LOGIN);
      return;
    }
    await dispatch(removeFromWishlist({ productId: product._id }));
  };

  if (!token) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Sign in to view your wishlist
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Save your favourite items and revisit them anytime.
        </Typography>
        <Stack direction="row" spacing={1.5} justifyContent="center">
          <MuiButton variant="contained" onClick={() => navigate(`${ROUTES.LOGIN}?redirect=${encodeURIComponent(ROUTES.WISHLIST)}`)}>
            Login
          </MuiButton>
          <MuiButton variant="outlined" component={RouterLink} to={ROUTES.PRODUCTS}>
            Continue shopping
          </MuiButton>
        </Stack>
      </Box>
    );
  }

  if (loading && !wishlist) {
    return <Typography>Loading wishlist...</Typography>;
  }

  if (products.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Your wishlist is empty
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tap the heart on products to save items you love.
        </Typography>
        <MuiButton variant="contained" component={RouterLink} to={ROUTES.PRODUCTS}>
          Explore collections
        </MuiButton>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Wishlist
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {products.length} saved item{products.length > 1 ? 's' : ''}
          </Typography>
        </Box>
        <MuiButton
          variant="outlined"
          color="error"
          onClick={() => dispatch(clearMyWishlist())}
          disabled={mutating}
        >
          Clear wishlist
        </MuiButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ProductGrid
        products={products}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleToggleWishlist}
      />
    </Box>
  );
};

export default WishlistPage;
